import { Box, FormikSelectInputNative, SelectInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import { kwhPriceFormatter } from '../../../../helpers/currencyFormatter';
import { percentageFormatter } from '../../../../helpers/percentageFormatter';
import { useGetPriceSheetsQuery, useLazyGetPriceSheetDetailsQuery } from '../../../../services/pricing';

export interface PricingFormValues {
  priceSheet: string;
  escalationRateMin: string;
  escalationRateMax: string;
  pvOnlyMin: string;
  pvOnlyCeiling: string;
  zeroEscalatorCeiling: string;
  absoluteCeiling: string;
}

export const BulkEditPricingFormSection = () => {
  const { setFieldValue, isSubmitting, values, touched, errors, handleBlur } = useFormikContext<PricingFormValues>();
  const { programType } = useParams<{ programType: string }>();
  const { priceSheet } = values;
  const { data: priceSheetResults } = useGetPriceSheetsQuery({
    pageNum: 1,
    pageSize: 1000,
    programType: programType as ProgramType,
  });
  const [trigger, { data: priceSheetDetails = {} }] = useLazyGetPriceSheetDetailsQuery();

  useEffect(() => {
    if (priceSheetResults?.data?.length && priceSheet) {
      trigger({ id: priceSheet });
    }
  }, [priceSheet, priceSheetResults?.data, trigger]);

  const priceSheetOptions = useMemo(
    () =>
      priceSheetResults?.data?.map((priceSheet) => ({
        label: priceSheet.name,
        value: priceSheet.id,
      })) ?? [],
    [priceSheetResults?.data],
  );

  const kwhRateOptions = useMemo(() => {
    const kwhRates = new Set();
    priceSheetDetails?.prices?.forEach((p: any) => {
      kwhRates.add(p.kwhRate);
    });
    return Array.from(kwhRates)
      .toSorted((a, b) => Number(a) - Number(b))
      .map((rate: any) => ({ label: kwhPriceFormatter.format(rate), value: rate }));
  }, [priceSheetDetails?.prices]);

  const escRateOptions = useMemo(() => {
    const escRates = new Set();
    priceSheetDetails?.prices?.forEach((p: any) => {
      escRates.add(p.escalationRate);
    });
    return Array.from(escRates)
      .toSorted((a, b) => Number(a) - Number(b))
      .map((rate: any) => ({ label: percentageFormatter.format(rate), value: rate }));
  }, [priceSheetDetails?.prices]);

  const handlePriceSheetMappingSelection = useCallback(
    (event: any) => {
      const id = event?.target?.value?.value ?? '';
      setFieldValue('priceSheet', id);
      setFieldValue('escalationRateMin', '');
      setFieldValue('escalationRateMax', '');
      setFieldValue('pvOnlyMin', '');
      setFieldValue('pvOnlyCeiling', '');
      setFieldValue('zeroEscalatorCeiling', '');
      setFieldValue('absoluteCeiling', '');
      if (id) {
        trigger({ id });
      }
    },
    [setFieldValue, trigger],
  );

  const selectedPriceSheetOption = useMemo(
    () => priceSheetOptions.find((option: { value: string }) => option.value === priceSheet) ?? null,
    [priceSheetOptions, priceSheet],
  );

  return (
    <Box childGap="lg">
      <SelectInput
        id="priceSheet"
        name="priceSheet"
        label="Price Sheet"
        value={selectedPriceSheetOption}
        options={priceSheetOptions}
        onChange={handlePriceSheetMappingSelection}
        onBlur={handleBlur}
        isDisabled={isSubmitting}
        error={touched.priceSheet && (errors.priceSheet as string)}
        isRequired
      />
      <Box direction="row" childGap="lg">
        <Field
          name="escalationRateMin"
          id="escalationRateMin"
          label="Escalator Min"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="escalationRateMax"
          id="escalationRateMax"
          label="Escalator Max"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="pvOnlyMin"
          id="pvOnlyMin"
          label="PV-Only Min"
          component={FormikSelectInputNative}
          options={kwhRateOptions}
          isDisabled={!kwhRateOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="pvOnlyCeiling"
          id="pvOnlyCeiling"
          label="PV-Only Max"
          component={FormikSelectInputNative}
          options={kwhRateOptions}
          isDisabled={!kwhRateOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="zeroEscalatorCeiling"
          id="zeroEscalatorCeiling"
          label="Zero Esc Max"
          component={FormikSelectInputNative}
          options={kwhRateOptions}
          isDisabled={!kwhRateOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="absoluteCeiling"
          id="absoluteCeiling"
          label="Absolute Max"
          component={FormikSelectInputNative}
          options={kwhRateOptions}
          isDisabled={!kwhRateOptions.length || isSubmitting}
          isRequired
        />
      </Box>
    </Box>
  );
};
